import React, { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Loader2, File } from "lucide-react";
import { pdf } from '@react-pdf/renderer';
import { DistributionAnalyticsPDF } from './pdf/DistributionAnalyticsPDF';
import { showErrorToast, showSuccessToast } from '../toast/toast';
import SpiderChart, { SpiderChartRef } from './spider-chart';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { backendApiUrl } from '@/lib/config';
import oktaService from '@/services/OktaService';
import { getKnnRowSourceType, getKnnValue, OVERALL_DECISION_ALIASES } from '@/lib/knnResponse';

// Helper function to convert Azure blob URL to backend proxy URL
const convertToProxyUrl = (azureBlobUrl: string): string | null => {
    if (!azureBlobUrl) return null;

    try {
        // Parse the Azure blob URL
        const url = new URL(azureBlobUrl);

        // Extract container name from hostname and path
        const pathParts = url.pathname.split('/');
        if (pathParts.length < 2) return null;

        // First part after domain is container name
        const containerName = pathParts[1]; // 'distribution-analytics-files'

        // Rest is the blob name (join remaining parts and decode)
        const blobName = pathParts.slice(2).join('/');
        const decodedBlobName = decodeURIComponent(blobName);

        // Create proxy URL with container parameter
        let proxyUrl = `media/image/${encodeURIComponent(decodedBlobName)}?containerName=${encodeURIComponent(containerName)}`;
        proxyUrl = proxyUrl.replace(/%2/g, '%252').replace(/\?/g, '%3F').replace(/=/g, '%3D');

        console.log('Converted Azure URL to proxy URL:', { azureBlobUrl, proxyUrl, containerName, blobName: decodedBlobName });

        return proxyUrl;
    } catch (error) {
        console.error('Error converting Azure URL to proxy URL:', error, azureBlobUrl);
        return null;
    }
};

// Helper function to fetch image data through proxy and convert to base64
const fetchImageAsBase64 = async (proxyUrl: string): Promise<string | null> => {
    if (!proxyUrl) return null;

    try {
        console.log('Fetching image through proxy:', proxyUrl);
        const oktaUser = await oktaService.getUser();

        const response = await fetch(proxyUrl,{
            headers:{
                Authorization: `Bearer ${oktaUser.access_token}`,
                'Accept': 'application/json',
                // Don't set Content-Type header with FormData - browser will set it with boundary
              },
        });
        
        if (!response.ok) {
            console.error('Failed to fetch image:', response.status, response.statusText);
            return null;
        }

        // Get the blob data
        const blob = await response.blob();

        // Convert blob to base64
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                console.log('Successfully converted image to base64, size:', result.length, 'chars');
                resolve(result);
            };
            reader.onerror = () => {
                console.error('Error converting blob to base64');
                reject(null);
            };
            reader.readAsDataURL(blob);
        });

    } catch (error) {
        console.error('Error fetching image through proxy:', error);
        return null;
    }
};

const hasDisplayValue = (value: unknown) => {
    const text = String(value ?? '').trim();
    return text !== '' && text !== '0' && text.toLowerCase() !== 'no data' && text.toLowerCase() !== 'n/a';
};

const formatProductForReport = (fileName: unknown, brand: unknown, volume: unknown, volumeUnit: unknown, fallback: unknown) => {
    if (hasDisplayValue(fileName)) return String(fileName).trim();
    return [
        hasDisplayValue(brand) ? String(brand).trim() : '',
        hasDisplayValue(volume) ? `${String(volume).trim()}${hasDisplayValue(volumeUnit) ? ` ${String(volumeUnit).trim()}` : ''}` : '',
    ].filter(Boolean).join(' ') || String(fallback || '');
};

const getChosenCaseSelectionValue = (chosenCase: any) => (
    String(chosenCase?.id ?? `${chosenCase?.project_name || ''}::${chosenCase?.pallet_variant || ''}`)
);

interface DistributionAnalyticsDownloadButtonProps {
    requestItem: any; // The main request item with all project data
    chooseTableData: any[]; // Data from the choose stage table
    spiderChartData: any; // Data for the spider chart
    selectedCases: any[]; // Selected cases from chosen cases
    knnData: any; // KNN data for option details
    tempUrlsData: any[]; // Temp URLs for images
    evaluateResult: any; // Results from evaluation
    selectedCase: string; // Selected case from chosen cases
    chartImageUrl?: string; // Chart image as base64 string
}

export const DistributionAnalyticsDownloadButton: React.FC<DistributionAnalyticsDownloadButtonProps> = ({
    requestItem,
    chooseTableData,
    spiderChartData,
    selectedCases,
    selectedCase,
    knnData,
    tempUrlsData,
    evaluateResult,
    chartImageUrl
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [generationStep, setGenerationStep] = useState('');
    const spiderChartRef = useRef<SpiderChartRef>(null);
    const [capturedChartImage, setCapturedChartImage] = useState<string | null>(null);
    const formatOptionData = (caseProjectName: string, palletVariant?: string) => {
        if (!knnData?.data) return null;

        const columns = knnData.columns || [];
        const rowIndex = knnData.data.findIndex((dataRow: any[]) => {
            if (dataRow[0] !== caseProjectName) return false;
            if (!palletVariant) return true;

            const rowVariant = getKnnValue(dataRow, columns, ['Pallet Variant', 'Variable Name', 'Variable', 'Test Variable']);
            return rowVariant === palletVariant;
        });
        if (rowIndex === -1) return null;

        const rowData = knnData.data[rowIndex];
        const getColumnValue = (columnNames: string[]) => {
            const value = getKnnValue(rowData, columns, columnNames);
            return value === undefined || value === null ? '' : String(value);
        };
        const volume = getColumnValue(['Volume(ml)', 'Volume']);
        const volumeUnit = getColumnValue(['VolumeUnits', 'Volume Units']) || (volume ? 'ml' : '');
        const fileName = getColumnValue(['File Name', 'Filename', 'Product File Name']);
        const brand = getColumnValue(['Brand']) || rowData[1] || '';

        return {
            optionNo: caseProjectName,
            sourceType: getKnnRowSourceType(rowData, columns, caseProjectName),
            product: formatProductForReport(fileName, brand, volume, volumeUnit, getColumnValue(['Category']) || rowData[2] || ''),
            palletVariant: getColumnValue(['Pallet Variant', 'Variable Name', 'Variable', 'Test Variable']),
            numberOfPallets: getColumnValue(['Number Of Pallets', 'Number of Pallets']),
            overallDecision: getColumnValue(OVERALL_DECISION_ALIASES),
            brand: getColumnValue(['Brand']),
            category: getColumnValue(['Category']),
            packageMaterial: getColumnValue(['Package Material', 'Primary Package Type']),
            volume,
            filledWeight: getColumnValue(['FilledWeight(g)', 'Filled Weight(g)', 'Filled Weight']),
            diameter: getColumnValue(['Diameter(in)', 'Diameter', 'Bottle Diameter', 'Bottle Diameter(in)']),
            height: getColumnValue(['Height(in)', 'Height', 'Bottle Height', 'Bottle Height(in)']),
            bottleDesign: getColumnValue(['Bottle Design']),
            itemsCase: getColumnValue(['Items/Case', 'Items Per Case']),
            caseLength: getColumnValue(['CaseLength(in)', 'Case Length(in)', 'Case Length', 'TertiaryCaseLength(in)', 'Tertiary Case Length(in)', 'Tertiary Case Length']),
            caseWidth: getColumnValue(['CaseWidth(in)', 'Case Width(in)', 'Case Width', 'TertiaryCaseWidth(in)', 'Tertiary Case Width(in)', 'Tertiary Case Width']),
            caseHeight: getColumnValue(['CaseHeight(in)', 'Case Height(in)', 'Case Height', 'TertiaryCaseHeight(in)', 'Tertiary Case Height(in)', 'Tertiary Case Height']),
            tertiaryPackageType: getColumnValue(['TertiaryPackageType', 'Tertiary Package Type']),
            tertiaryLength: getColumnValue(['TertiaryCaseLength(in)', 'Tertiary Case Length(in)', 'Tertiary Case Length']),
            tertiaryWidth: getColumnValue(['TertiaryCaseWidth(in)', 'Tertiary Case Width(in)', 'Tertiary Case Width']),
            tertiaryHeight: getColumnValue(['TertiaryCaseHeight(in)', 'Tertiary Case Height(in)', 'Tertiary Case Height']),
            palletTypes: getColumnValue(['Pallet Types', 'Pallet Dimension', 'Pallet Dimension (inches)']),
            palletMaterial: getColumnValue(['Pallet Material']),
            unitLoadLength: getColumnValue(['UnitloadLength(in)', 'Unit Load Length(in)', 'Unit Load Length']),
            unitLoadWidth: getColumnValue(['UnitloadWidth(in)', 'Unit Load Width(in)', 'Unit Load Width']),
            unitLoadHeight: getColumnValue(['UnitloadHeight(in)', 'Unit Load Height(in)', 'Unit Load Height']),
            casesPerLayer: getColumnValue(['Cases Per Layer', 'Cases/Layer']),
            layersPerPallet: getColumnValue(['Layers/Pallet', 'Layers Per Pallet']),
            itemsPallet: getColumnValue(['Items/pallet', 'Items/Pallet', 'Items Per Pallet']),
            palletPatternStack: getColumnValue(['Pallet Pattern Stackup', 'Pallet Pattern', 'Pallet Pattern Design']),
            testResults: {
                preShipLean: getColumnValue(['PreShip_Lean', 'Pre Ship Lean']),
                postShipLean: getColumnValue(['PostShip_Lean', 'Post Ship Lean']),
                postShipWalk: getColumnValue(['PostShip_Walk', 'Post Ship Walk']),
                postStackLean: getColumnValue(['PostStack_Lean', 'Post Stack Lean']),
                postStackCompressionRebound: getColumnValue(['Post Stack Compression_Rebound', 'PostStackCompressionRebound', 'Post Stack Compression Rebound']),
            },
            ftl: {
                preShip: {
                    top: getColumnValue(["('PreShipForceToload', 'TOP ')", "('PreShipForceToload', 'TOP')", 'PreShipForceToLoad TOP', 'PreShipForceToload TOP', 'PreShip Force To Load TOP', 'PreShip FTL Top']),
                    middle: getColumnValue(["('PreShipForceToload', 'Middle')", "('PreShipForceToload', 'Middle ')", 'PreShipForceToLoad Middle', 'PreShipForceToload Middle', 'PreShip Force To Load Middle', 'PreShip FTL Middle']),
                    bottom: getColumnValue(["('PreShipForceToload', 'Bottom')", 'PreShipForceToLoad Bottom', 'PreShipForceToload Bottom', 'PreShip Force To Load Bottom', 'PreShip FTL Bottom']),
                },
                postShip: {
                    top: getColumnValue(["('PostShipForceToload', 'TOP ')", "('PostShipForceToload', 'TOP')", 'PostShipForceToLoad TOP', 'PostShipForceToload TOP', 'PostShip Force To Load TOP', 'PostShip FTL Top']),
                    middle: getColumnValue(["('PostShipForceToload', 'Middle ')", "('PostShipForceToload', 'Middle')", 'PostShipForceToLoad Middle', 'PostShipForceToload Middle', 'PostShip Force To Load Middle', 'PostShip FTL Middle']),
                    bottom: getColumnValue(["('PostShipForceToload', 'Bottom')", 'PostShipForceToLoad Bottom', 'PostShipForceToload Bottom', 'PostShip Force To Load Bottom', 'PostShip FTL Bottom']),
                },
            },
            palletImage: tempUrlsData?.find(url => url.folder === caseProjectName && url.filename?.toLowerCase()?.includes('isometric'))?.url || '',
            primaryFiles: tempUrlsData?.filter(url => url.folder === caseProjectName && url.filename?.toLowerCase()?.includes('primary package'))?.map(url => url) || [],
            secondaryFiles: tempUrlsData?.filter(url => url.folder === caseProjectName && url.filename?.toLowerCase()?.includes('secondary package'))?.map(url => url) || [],
            unitFiles: tempUrlsData?.filter(url => url.folder === caseProjectName && url.filename?.toLowerCase()?.includes('unit load'))?.map(url => url) || [],
        };
    };

    const formatEvaluationResults = (evaluateResult: any) => {
        if (!evaluateResult || !Array.isArray(evaluateResult)) return null;

        // Transform the evaluation result into the expected format
        // evaluateResult is an array with LinearRegression and TreeModel data
        const transportModes = evaluateResult.map((result, index) => {
            const results = [];

            // Add LinearRegression results
            if (result.LinearRegression) {
                results.push(
                    {
                        metric: 'Lean (Linear Regression)',
                        value: result.LinearRegression.Lean || 'N/A',
                        status: 'pass', // You can add logic to determine status based on values
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Walk (Linear Regression)',
                        value: result.LinearRegression.Walk || 'N/A',
                        status: 'pass',
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Compression (Linear Regression)',
                        value: result.LinearRegression.Compression || 'N/A',
                        status: 'pass',
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Safety Factor (Linear Regression)',
                        value: result.LinearRegression.SafetyFactor || 'N/A',
                        status: 'pass',
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Overall Risk (Linear Regression)',
                        value: result.LinearRegression.OverallRisk || 'N/A',
                        status: result.LinearRegression.OverallRisk === 'High' ? 'fail' :
                            result.LinearRegression.OverallRisk === 'Medium' ? 'warning' : 'pass',
                        threshold: 'N/A'
                    }
                );
            }

            // Add TreeModel results
            if (result.TreeModel) {
                results.push(
                    {
                        metric: 'Lean (Random Forest)',
                        value: result.TreeModel.Lean || 'N/A',
                        status: 'pass',
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Walk (Random Forest)',
                        value: result.TreeModel.Walk || 'N/A',
                        status: 'pass',
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Compression (Random Forest)',
                        value: result.TreeModel.Compression || 'N/A',
                        status: 'pass',
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Safety Factor (Random Forest)',
                        value: result.TreeModel.SafetyFactor || 'N/A',
                        status: 'pass',
                        threshold: 'N/A'
                    },
                    {
                        metric: 'Overall Risk (Random Forest)',
                        value: result.TreeModel.OverallRisk || 'N/A',
                        status: result.TreeModel.OverallRisk === 'High' ? 'fail' :
                            result.TreeModel.OverallRisk === 'Medium' ? 'warning' : 'pass',
                        threshold: 'N/A'
                    }
                );
            }

            return {
                mode: `${result.TransitMode || 'Unknown'} - ${result.Distance || 'Unknown'} miles`,
                results: results
            };
        });

        // Calculate summary statistics
        const allResults = transportModes.flatMap(tm => tm.results);
        const totalTests = allResults.length;
        const passed = allResults.filter(r => r.status === 'pass').length;
        const failed = allResults.filter(r => r.status === 'fail').length;
        const warnings = allResults.filter(r => r.status === 'warning').length;

        // Determine overall risk based on the worst case
        let overallRisk = 'low';
        if (failed > 0) overallRisk = 'high';
        else if (warnings > 0) overallRisk = 'medium';

        return {
            transportModes,
            summary: {
                totalTests,
                passed,
                failed,
                warnings,
                overallRisk: overallRisk as 'low' | 'medium' | 'high'
            },
            analysisDate: new Date().toLocaleDateString()
        };
    };

    const captureChartImage = (): Promise<string | null> => {
        return new Promise((resolve) => {
            // Give the chart time to render
            setTimeout(() => {
                if (spiderChartRef.current) {
                    const image = spiderChartRef.current.getChartImage();
                    setCapturedChartImage(image);
                    resolve(image);
                } else {
                    resolve(null);
                }
            }, 2000); // Wait 2 seconds for chart to fully render
        });
    };

    const handleDownload = async () => {
        try {
            setIsGenerating(true);
            setShowModal(true);

            // Step 1: Show chart generation step
            setGenerationStep('Preparing chart visualization...');

            // Wait a moment for modal to show and chart to render
            await new Promise(resolve => setTimeout(resolve, 500));

            // Step 2: Capture chart image (keep this as it usually works)
            setGenerationStep('Capturing chart image...');
            const chartImage = await captureChartImage();

            // Step 3: Generate PDF
            setGenerationStep('Generating PDF report...');

            // Use the captured chart image or fall back to the existing chartImageUrl
            const imageToUse = chartImage || chartImageUrl;
            const selectedCasesForReport = selectedCases.filter(chosenCase => (
                getChosenCaseSelectionValue(chosenCase) === selectedCase || chosenCase.project_name === selectedCase
            ));
            const reportCases = selectedCasesForReport.length > 0 ? selectedCasesForReport : selectedCases;
            const formattedSelectedCases = await Promise.all(reportCases.map(async (chosenCase) => {
                const optionData = formatOptionData(chosenCase.project_name, chosenCase.pallet_variant);
                const originalPalletImageUrl = optionData?.palletImage;
                const palletImageProxyUrl = originalPalletImageUrl ? convertToProxyUrl(originalPalletImageUrl) : null;
                const palletImageData = palletImageProxyUrl
                    ? await fetchImageAsBase64(`${backendApiUrl}/${palletImageProxyUrl}`)
                    : null;
                const safeOptionData = optionData || {
                    optionNo: chosenCase.project_name,
                    product: chosenCase.project_name,
                    palletVariant: chosenCase.pallet_variant,
                };

                return {
                    project_name: chosenCase.project_name,
                    optionData: { ...safeOptionData, palletImage: palletImageData || palletImageProxyUrl || originalPalletImageUrl || '' },
                    palletDistributionData: chosenCase,
                };
            }));

            // Prepare the data for PDF generation
            const pdfData = {
                projectData: {
                    // Define stage data
                    project_name: requestItem.project_name || '',
                    brand_name: requestItem.brand_name || '',
                    location_name: requestItem.location_name || '',
                    customer: requestItem.customer || '',
                    application_name: requestItem.application_name || '',
                    primary_package_type: requestItem.primary_package_type || '',
                    category: requestItem.category || '',
                    net_contents: requestItem.net_contents || '',
                    net_contents_unit: requestItem.net_contents_unit || '',
                    filled_top_load: requestItem.filled_top_load || '',
                    filled_top_load_unit: requestItem.filled_top_load_unit || '',
                    primary_package_diameter: requestItem.primary_package_diameter || '',
                    primary_package_diameter_unit: requestItem.primary_package_diameter_unit || '',
                    primary_package_height: requestItem.primary_package_height || '',
                    primary_package_height_unit: requestItem.primary_package_height_unit || '',
                    bottle_design: requestItem.bottle_design || '',
                    has_tertiary_package: requestItem.has_tertiary_package || '',
                    items_per_case: requestItem.items_per_case || '',
                    pack_per_case: requestItem.pack_per_case || '',
                    pallet_base_type: requestItem.pallet_base_type || '',

                    // Package stage data
                    secondary_package_type: requestItem.secondary_package_type || '',
                    secondary_topload_filled: requestItem.secondary_topload_filled || '',
                    secondary_topload_filled_unit: requestItem.secondary_topload_filled_unit || '',
                    secondary_weight: requestItem.secondary_weight || '',
                    secondary_weight_unit: requestItem.secondary_weight_unit || '',
                    secondary_length: requestItem.secondary_length || '',
                    secondary_length_unit: requestItem.secondary_length_unit || '',
                    secondary_width: requestItem.secondary_width || '',
                    secondary_width_unit: requestItem.secondary_width_unit || '',
                    secondary_height: requestItem.secondary_height || '',
                    secondary_height_unit: requestItem.secondary_height_unit || '',

                    // Tertiary package data
                    tertiary_material: requestItem.tertiary_material || '',
                    tertiary_bct: requestItem.tertiary_bct || '',
                    tertiary_bct_unit: requestItem.tertiary_bct_unit || '',
                    tertiary_length: requestItem.tertiary_length || '',
                    tertiary_length_unit: requestItem.tertiary_length_unit || '',
                    tertiary_width: requestItem.tertiary_width || '',
                    tertiary_width_unit: requestItem.tertiary_width_unit || '',
                    tertiary_height: requestItem.tertiary_height || '',
                    tertiary_height_unit: requestItem.tertiary_height_unit || '',
                    tertiary_weight: requestItem.tertiary_weight || '',
                    tertiary_weight_unit: requestItem.tertiary_weight_unit || '',

                    k_value: requestItem.k_value || '5'
                },
                chooseTableData: chooseTableData || [],
                spiderChartData: spiderChartData || {},
                spiderChartImageUrl: imageToUse,
                selectedCases: formattedSelectedCases,
                createdAt: requestItem.created_at || new Date().toISOString(),
                projectId: requestItem.id || 'unknown'
            };

            // Generate PDF
            const pdfDoc = <DistributionAnalyticsPDF {...pdfData} evaluateResult={evaluateResult} />;
            const blob = await pdf(pdfDoc).toBlob();

            // Download the PDF
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `distribution-analytics-report-${Date.now()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setShowModal(false);
            showSuccessToast("Distribution Analytics report downloaded successfully");
        } catch (error) {
            console.error('Error generating PDF:', error);
            setShowModal(false);
            showErrorToast("Failed to generate PDF report. Please try again.");
        } finally {
            setIsGenerating(false);
            setGenerationStep('');
            setCapturedChartImage(null);
        }
    };

    return (
        <>
            <Button
                onClick={handleDownload}
                disabled={isGenerating}
                className="bg-blue flex items-center gap-2"
            >
                {isGenerating ? (
                    <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        Generating PDF...
                    </>
                ) : (
                    <>
                        <File className="h-4 w-4" />
                        Generate Report
                    </>
                )}
            </Button>

            {/* PDF Generation Modal */}
            <Dialog open={showModal} onOpenChange={() => { }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-center">Generating Your Report</DialogTitle>
                    </DialogHeader>

                    <div className="flex flex-col items-center space-y-6 py-6">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        <p className="text-sm text-gray-600 text-center">{generationStep}</p>

                        {/* Hidden Spider Chart for Image Capture */}
                        <div className="opacity-0 absolute -top-[9999px] left-0 pointer-events-none">
                            {knnData && (
                                <SpiderChart
                                    ref={spiderChartRef}
                                    controls={false}
                                    data={knnData}
                                    chartWidth={600}
                                    chartHeight={600}
                                />
                            )}
                        </div>

                        <div className="text-xs text-gray-400 text-center">
                            Please wait while we prepare your comprehensive distribution analytics report...
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}; 
